import { supabase } from '../supabaseClient';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  loadSignupLegalDocumentsCache,
  saveSignupLegalDocumentsCache,
  signupLegalCacheMatchesMeta,
} from '../utils/signupLegalDocumentsCache';

export interface SignupLegalHtml {
  terms_html: string;
  privacy_html: string;
}

type SignupLegalMeta = {
  terms_updated_at: string;
  privacy_updated_at: string;
};

function parseMeta(raw: unknown): SignupLegalMeta {
  if (!raw || typeof raw !== 'object') {
    return { terms_updated_at: '', privacy_updated_at: '' };
  }
  const o = raw as Record<string, unknown>;
  return {
    terms_updated_at: typeof o.terms_updated_at === 'string' ? o.terms_updated_at : '',
    privacy_updated_at: typeof o.privacy_updated_at === 'string' ? o.privacy_updated_at : '',
  };
}

function parseDocuments(raw: unknown): SignupLegalHtml & SignupLegalMeta {
  if (!raw || typeof raw !== 'object') {
    return { terms_html: '', privacy_html: '', terms_updated_at: '', privacy_updated_at: '' };
  }
  const o = raw as Record<string, unknown>;
  return {
    terms_html: typeof o.terms_html === 'string' ? o.terms_html : '',
    privacy_html: typeof o.privacy_html === 'string' ? o.privacy_html : '',
    terms_updated_at: typeof o.terms_updated_at === 'string' ? o.terms_updated_at : '',
    privacy_updated_at: typeof o.privacy_updated_at === 'string' ? o.privacy_updated_at : '',
  };
}

async function fetchSignupLegalMeta(): Promise<SignupLegalMeta | null> {
  const { data, error } = await supabase.rpc('get_signup_legal_documents_meta');
  if (error) {
    // 마이그레이션 전 환경: 메타 RPC 없으면 null → 본문 RPC로 폴백
    console.warn('get_signup_legal_documents_meta:', error.message);
    return null;
  }
  return parseMeta(data);
}

async function fetchSignupLegalDocumentsNetwork(): Promise<SignupLegalHtml & SignupLegalMeta> {
  const { data, error } = await supabase.rpc('get_signup_legal_documents');
  if (error) {
    console.warn('get_signup_legal_documents:', error.message);
    return { terms_html: '', privacy_html: '', terms_updated_at: '', privacy_updated_at: '' };
  }
  return parseDocuments(data);
}

/**
 * 비로그인 회원가입·랜딩용 약관/개인정보.
 * 메타(updated_at)가 캐시와 같으면 본문은 LS 재사용, 다르면 네트워크에서 갱신.
 * terms_updated_at/privacy_updated_at은 동의 기록(user_legal_consents)의 버전 값으로 쓰인다.
 */
export async function fetchSignupLegalDocuments(): Promise<SignupLegalHtml & SignupLegalMeta> {
  const cached = loadSignupLegalDocumentsCache();
  const meta = await fetchSignupLegalMeta();

  if (meta && signupLegalCacheMatchesMeta(cached, meta)) {
    if (process.env.NODE_ENV === 'development') {
      recordDevLocalStorageProgramCacheUse('회원가입약관·본문·LS');
    }
    return {
      terms_html: cached!.terms_html,
      privacy_html: cached!.privacy_html,
      terms_updated_at: cached!.terms_updated_at,
      privacy_updated_at: cached!.privacy_updated_at,
    };
  }

  const fresh = await fetchSignupLegalDocumentsNetwork();
  const terms_updated_at = fresh.terms_updated_at || meta?.terms_updated_at || '';
  const privacy_updated_at = fresh.privacy_updated_at || meta?.privacy_updated_at || '';
  // 메타 RPC가 없고 본문에도 버전이 없으면 캐시만 갱신(가능하면)
  if (terms_updated_at || privacy_updated_at || fresh.terms_html || fresh.privacy_html) {
    saveSignupLegalDocumentsCache({
      terms_html: fresh.terms_html,
      privacy_html: fresh.privacy_html,
      terms_updated_at,
      privacy_updated_at,
    });
  }
  return {
    terms_html: fresh.terms_html,
    privacy_html: fresh.privacy_html,
    terms_updated_at,
    privacy_updated_at,
  };
}
